interface CallbackObject {
  // when the element is entering the view
  onEnter?: (() => void) | undefined;
  // when the element is leaving the view
  onLeave?: (() => void) | undefined;
  // when the elemnt is entering or leaving the view
  // this is trigger each time onEnter/ onLeave is triggered
  onChange?: () => void;
}

export type Observe = (element: Element, callbackObj: CallbackObject) => void;

export type Unobserve = (element: Element) => void;

export type Observer = {
  observe: Observe;
  unobserve: Unobserve;
};

const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);

export function createIntersectionObserver({
  root,
  rootMargin,
  threshold,
}: IntersectionObserverInit): {
  observe: Observe;
  unobserve: Unobserve;
} {
  // we do not need observe/unobserve in SSR
  // so stub it with empty function
  if (!canUseDOM) {
    return {
      observe: () => undefined,
      unobserve: () => undefined,
    };
  }
  const elementToCallbackMap = new WeakMap<Element, CallbackObject>();
  const initialStatusDeterminedSet = new WeakSet<Element>();

  const observerInstance = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const { onEnter, onLeave, onChange } =
          elementToCallbackMap.get(entry.target) || {};
        const notInitialStatus = initialStatusDeterminedSet.has(entry.target);

        if (entry.isIntersecting) {
          onEnter !== undefined && onEnter();
        } else if (notInitialStatus) {
          onLeave !== undefined && onLeave();
        }
        initialStatusDeterminedSet.add(entry.target);
        onChange !== undefined && onChange();
      }
    },
    {
      root,
      rootMargin,
      threshold,
    }
  );

  const observe = (element: Element, callbackObj: CallbackObject) => {
    elementToCallbackMap.set(element, callbackObj);
    observerInstance.observe(element);
  };

  const unobserve = (element: Element) => {
    // check if element exists in Map, remove then unobserve
    if (elementToCallbackMap.has(element)) {
      elementToCallbackMap.delete(element);
      observerInstance.unobserve(element);
    }
  };

  return {
    observe,
    unobserve,
  };
}
